﻿import {computed, nextTick, ref, watch} from 'vue';
import {defineStore, storeToRefs} from 'pinia';

import type {
    PreviewTab,
    PreviewTabName,
} from '@/components/generic/PreviewTab.ts';
import {useMusicUploadStore} from '@/stores/useMusicUploadStore.ts';
import {currentMainTab, type MainTab, navigateToMainTab} from '@/router/mainTabNavigation';

export const useTabStore = defineStore('previewTabs', () => {
    const musicUploadStore = useMusicUploadStore();

    const {
        isConverting,
        isHavingFile,
        isUploading,
        isAnalyzing,
        isTransposing,
        isHavingGriffschriftResult,
        isConversionStale,
        currentPdfObjectUrl,
        fileInfo,
        selectedFileContent,
    } = storeToRefs(musicUploadStore);

    const activePreviewTab = ref<PreviewTabName>('input');
    const switchToConvertedTabWhenFinished = ref(false);

    watch(
        [isHavingFile, isHavingGriffschriftResult],
        ([hasFile, hasResult]) => {
            if (!hasFile) {
                activePreviewTab.value = 'input';
                return;
            }

            if (
                activePreviewTab.value === 'converted'
                && !hasResult
                && !isConverting.value
            ) {
                activePreviewTab.value = 'input';
            }
        },
    );

    // Einziger Owner des Auto-Wechsels zur Griffschrift nach einer Konvertierung (startConversion
    // switcht selbst NICHT mehr). Nur bei vorhandenem Ergebnis wechseln – bei Fehlschlag/Abbruch
    // bleibt die Eingabe-Vorschau stehen.
    watch(isConverting, (isCurrentlyConverting) => {
        if (isCurrentlyConverting) {
            switchToConvertedTabWhenFinished.value = true;
            return;
        }

        if (!switchToConvertedTabWhenFinished.value) {
            return;
        }

        switchToConvertedTabWhenFinished.value = false;
        if (isHavingGriffschriftResult.value) {
            activePreviewTab.value = 'converted';
        }
    });

    /*
     * DER eine Ort für „Input hat sich geändert → Eingabe-Ansicht zeigen" (ersetzt die früher
     * über sechs Stellen verstreuten `switchToInputTab()`-Aufrufe – Upload-Store, file.util,
     * Panels, Verlauf-Reuse – und damit auch den Upload↔Tab-Store-Importzyklus):
     *
     * - `fileInfo` ändert sich beim Upload-START (createFileInfo) – sofortiges Feedback, bevor
     *   der Inhalt da ist (wichtig beim OMR-PDF-Upload: die PDF-Vorschau läuft 30–120 s auf der
     *   Eingabe-Vorschau, während Audiveris arbeitet).
     * - `selectedFileContent` ändert sich bei jedem Ergebnis: Upload, manueller Edit, Transpose,
     *   diatonische Verschiebung, Merge, Bass-System, Akkorderkennung, Undo/Restore, Reuse.
     *
     * Solange eine Arbeitsdatei existiert, wird zusätzlich der Haupttab „Konvertieren" aktiv
     * (deckt Reuse aus dem Verlauf-Tab und den window-weiten Datei-Drop auf anderen Tabs ab).
     * Nach `reset()` (beides null) bleibt der Haupttab stehen, nur die Vorschau geht auf „input".
     */
    watch([fileInfo, selectedFileContent], ([info, content]) => {
        activePreviewTab.value = 'input';
        if ((info || content) && mainTabSwitchLocks === 0) activeMainTab.value = 'convert';
    });

    // Der Start-Restore aus der URL (App.vue) läuft hierdurch: ein Reload auf `/akkorde?file=…`
    // soll auf den Akkorden bleiben, statt wegen des wiederhergestellten Inhalts nach „Konvertieren"
    // zu springen. Der Restore setzt `fileInfo` sofort und den Inhalt erst nach dem Netzwerk –
    // deshalb gibt die Sperre erst nach `fn` UND einem Tick frei (beide Watcher-Läufe gesperrt).
    let mainTabSwitchLocks = 0;
    async function withoutMainTabSwitch<T>(fn: () => Promise<T>): Promise<T> {
        mainTabSwitchLocks += 1;
        try {
            return await fn();
        } finally {
            await nextTick();
            mainTabSwitchLocks -= 1;
        }
    }

    const tabs = computed<Record<PreviewTabName, PreviewTab>>(() => ({
        input: {
            name: 'input',
            icon: 'mdi-upload',
            isDisabled: false,
            isLoading:
                (
                    isAnalyzing.value
                    || isUploading.value
                    || isTransposing.value
                )
                && !currentPdfObjectUrl.value,
            label: 'Notenschrift',
        },
        converted: {
            name: 'converted',
            isHidden: !isHavingFile.value,
            icon: isConverting.value
                ? 'fa6-solid:spinner'
                : 'mdi-music-clef-treble',
            isLoading:
                isConverting.value
                || switchToConvertedTabWhenFinished.value,
            isDisabled: !isHavingGriffschriftResult.value,
            label: 'Griffschrift',
            // Die Ausgabe wird nach einer Eingabe-Änderung NICHT neu erzeugt (konvertiert wird nur
            // auf Klick) – sie bleibt stehen und weist hier auf ihren Stand hin.
            badge: isConversionStale.value ? 'veraltet' : undefined,
            // Diskriminator statt Komponente (PreviewTabsCard mappt lokal) – siehe PreviewTab.ts.
            disabledLabelFallback: 'convert-button',
        },
    }));

    const mainTabs = ref([
        {id: 'convert', label: 'Konvertieren', icon: 'fa6-solid:wand-magic-sparkles'},
        {id: 'history', label: 'Verlauf', icon: 'fa6-solid:clock-rotate-left'},
        {id: 'keyboard', label: 'Harmonika', icon: 'lucide:piano'},
        {id: 'chords', label: 'Akkorde', icon: 'lucide:compass'},
        {id: 'settings', label: 'Einstellungen', icon: 'fa6-solid:sliders'},
    ] as const);
    // Spiegel der Route (router/mainTabNavigation.ts): Lesen = aktueller Haupt-Tab, Schreiben = navigieren.
    const activeMainTab = computed<MainTab>({
        get: () => currentMainTab.value,
        set: navigateToMainTab,
    });
    return {
        mainTabs,
        activeMainTab,
        withoutMainTabSwitch,
        activePreviewTab,
        tabs,
        switchToConvertedTabWhenFinished,
    };
});